import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLanguageTransition } from "@/components/LanguageTransitionProvider";

// Lazy-load the R3F scene so it never blocks the loader's first paint.
// The copy, emblems and skip button paint immediately; the scene fades in
// over ~300ms once its chunk is ready.
const AwardsLoaderScene = lazy(() => import("@/components/AwardsLoaderScene"));

/**
 * First-visit awards loading screen.
 *
 * Trigger rules:
 *   - Shown once per browser, gated by localStorage flag
 *     `frac_awards_loader_seen_v1` (deliberately not bumped — a duration
 *     tweak should not resurrect the loader for returning visitors).
 *   - Suppressed while the Mandarin language transition is playing so
 *     the two overlays never overlap.
 *   - Auto-dismisses after 5.0s or when the user clicks Skip / hits
 *     Escape / Enter / Space.
 *   - `prefers-reduced-motion: reduce` snaps everything to the final
 *     state instantly but the 5s auto-dismiss timer still runs and the
 *     progress bar still fills linearly.
 *
 * Motion timeline (t in ms from mount):
 *     0     backdrop fade-in                200ms
 *     0     MDStatus emblem scale-in        600ms
 *   120     MBoR emblem scale-in            600ms  (120ms stagger)
 *   300     canvas fade-in                  300ms
 *   600     EN copy mask-reveal             600ms
 *  1600     ZH copy mask-reveal             600ms
 *  2400 ── all UI settled ──
 *  4600     exit fade + 12px y-drift        400ms
 *  5000     unmount
 *
 * Layering (bottom → top, all inside the loader root):
 *   [scene]        R3F canvas, ambient behavioural field
 *   [scrim1]       radial darkener behind copy
 *   [scrim2]       bottom vignette for progress hairline
 *   [content]      emblems / copy (constrained to leave a bottom
 *                   safe-zone reserved for the skip button)
 *   [skip]         fixed skip chip in the safe-zone
 *
 * // AwardsLoader is one of the two sites where the MBoR raster and its
 * // "Malaysia Book of Records" caption (which contains the token
 * // "IEO") may legitimately appear per the scoped exception.
 */
const STORAGE_KEY = "frac_awards_loader_seen_v1";
const DURATION_MS = 5000;

export function AwardsLoader() {
  const reduce = useReducedMotion();
  const langTransition = useLanguageTransition();
  const [open, setOpen] = useState(false);
  const [mountReady, setMountReady] = useState(false);
  const [sceneVisible, setSceneVisible] = useState(false);
  const skipBtnRef = useRef(null);
  const dismissedRef = useRef(false);

  useEffect(() => {
    setMountReady(true);
    if (typeof window === "undefined") return;
    try {
      const seen = window.localStorage.getItem(STORAGE_KEY);
      if (!seen && !langTransition.isActive) {
        setOpen(true);
      }
    } catch (_) {
      /* localStorage unavailable — skip loader silently */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch (_) {
      /* ignore */
    }
    // Reveal the canvas after a short beat so it fades in behind the
    // already-mounted foreground.
    const t = setTimeout(() => setSceneVisible(true), 300);
    return () => clearTimeout(t);
  }, [open]);

  const dismiss = useCallback(() => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(dismiss, DURATION_MS);
    return () => clearTimeout(t);
  }, [open, dismiss]);

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      skipBtnRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        dismiss();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!mountReady || typeof document === "undefined") return null;

  const enLine =
    "Frac is the proud awardee of the Malaysia Digital Status and the Malaysia Book of Records, and opening up a new category in the Malaysia Book of Records as well.";
  const zhLine =
    "Frac 荣获马来西亚数字地位（Malaysia Digital Status）及马来西亚纪录大全（Malaysia Book of Records）认证，并于马来西亚纪录大全中开创全新类别。";

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label="Frac recognition"
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center overflow-hidden bg-surface-void"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, y: reduce ? 0 : -12 }}
          transition={{
            duration: reduce ? 0.001 : 0.4,
            ease: [0.2, 0.8, 0.2, 1],
          }}
          data-testid="awards-loader"
        >
          {/* Layer 1: R3F ambient scene (lazy) */}
          <motion.div
            aria-hidden
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: sceneVisible ? 1 : 0 }}
            transition={{ duration: reduce ? 0 : 0.3, ease: "linear" }}
            data-testid="awards-loader-scene-layer"
          >
            <Suspense fallback={null}>
              <AwardsLoaderScene />
            </Suspense>
          </motion.div>

          {/* Layer 2: radial scrim behind copy */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(80% 60% at 50% 45%, rgba(2,4,11,0.35) 0%, rgba(2,4,11,0.72) 100%)",
            }}
          />
          {/* Layer 3: bottom vignette for progress hairline */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, transparent 60%, rgba(2,4,11,0.9) 100%)",
            }}
          />

          {/* Frac wordmark — removed. Loader stack now leads with the
              MDStatus emblem. */}

          {/* Content stack (emblems + copy) — vertically centred within
              the viewport MINUS a bottom safe-zone reserved for the
              skip button (96px mobile / 80px tablet+). Prevents the ZH
              copy from ever bleeding under the skip chip. */}
          <div
            className="relative z-10 flex min-h-screen w-full flex-col items-center justify-center px-6 pb-24 md:pb-20"
          >
            {/* Emblems */}
            <div
              className="flex flex-col items-center gap-8 min-[480px]:flex-row min-[480px]:gap-8 md:gap-16"
              data-testid="awards-loader-emblems"
            >
              <EmblemFadeIn
                reduce={reduce}
                delay={0}
                href="https://drive.google.com/file/d/1u6KArN1vFKPc5AJU6OSha_jx91TMwfBM/view"
                aria="Malaysia Digital Status certification (opens in new tab)"
              >
                <img
                  src="/awards/md-status-white.png"
                  alt="Malaysia Digital Status certification"
                  className="h-20 md:h-[120px] w-auto object-contain"
                  data-testid="awards-loader-md-status"
                />
              </EmblemFadeIn>
              <EmblemFadeIn
                reduce={reduce}
                delay={reduce ? 0 : 0.12}
                href="https://drive.google.com/file/d/1EkriAv1SjJjovoLqWaYksADjbNzLoaSQ/view?usp=sharing"
                aria="Malaysia Book of Records award (opens in new tab)"
              >
                <img
                  src="/awards/mbor-white.png"
                  alt="Malaysia Book of Records award"
                  data-emblem="mbor"
                  className="h-20 md:h-[120px] w-auto object-contain"
                  data-testid="awards-loader-mbor"
                />
              </EmblemFadeIn>
            </div>

            {/* Bilingual copy */}
            <div className="mt-8 md:mt-10 flex max-w-[32ch] md:max-w-[72ch] flex-col items-center text-center">
              <RevealLine
                delay={reduce ? 0 : 0.6}
                reduce={reduce}
                className="text-[17px] md:text-[20px] font-normal text-ink-primary"
                style={{ lineHeight: 1.5, letterSpacing: "-0.02em" }}
                data-testid="awards-loader-line-en"
              >
                {enLine}
              </RevealLine>

              <div
                aria-hidden
                className="my-4 md:my-5 h-6 w-px"
                style={{ background: "var(--border-hairline)" }}
              />

              <RevealLine
                delay={reduce ? 0 : 1.6}
                reduce={reduce}
                className="text-[16px] md:text-[18px] font-normal text-ink-primary"
                style={{ lineHeight: 1.6 }}
                data-testid="awards-loader-line-zh"
              >
                {zhLine}
              </RevealLine>
            </div>
          </div>

          {/* Skip button — reserved bottom safe-zone.
              Mobile: centred at bottom. Desktop: bottom-right. */}
          <button
            ref={skipBtnRef}
            type="button"
            onClick={dismiss}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 md:left-auto md:right-8 md:translate-x-0 z-[201] inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[14px] font-semibold text-ink-primary transition-colors duration-base ease-out hover:border-[color:var(--border-active)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frac-violet motion-reduce:transition-none"
            style={{
              borderColor: "var(--border-hairline)",
              background: "rgba(2, 4, 11, 0.6)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
            }}
            data-testid="awards-loader-skip"
            aria-label="Skip"
          >
            <span>Skip</span>
            <span aria-hidden style={{ color: "var(--text-tertiary)" }}>·</span>
            <span>跳过</span>
          </button>

          {/* Progress hairline */}
          <ProgressHairline duration={DURATION_MS} reduce={reduce} />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
/* sub-components                                                             */
/* -------------------------------------------------------------------------- */

function EmblemFadeIn({ reduce, delay, href, aria, children }) {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={aria}
      className="inline-flex items-center justify-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-frac-violet"
      initial={
        reduce ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.92 }
      }
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: reduce ? 0 : 0.6,
        delay,
        ease: [0.2, 0.8, 0.2, 1],
      }}
    >
      {children}
    </motion.a>
  );
}

function RevealLine({ children, delay, reduce, className, style, ...rest }) {
  return (
    <div style={{ overflow: "hidden" }}>
      <motion.p
        initial={reduce ? { y: 0, opacity: 1 } : { y: "100%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        transition={{
          duration: reduce ? 0 : 0.6,
          delay,
          ease: [0.2, 0.8, 0.2, 1],
        }}
        className={className}
        style={style}
        {...rest}
      >
        {children}
      </motion.p>
    </div>
  );
}

function ProgressHairline({ duration, reduce }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 bottom-0 z-10 h-px bg-transparent"
      data-testid="awards-loader-progress"
    >
      <motion.div
        className="h-full bg-frac-violet"
        initial={{ width: "0%" }}
        animate={{ width: "100%" }}
        transition={{
          duration: duration / 1000,
          ease: "linear",
        }}
      />
    </div>
  );
}

export default AwardsLoader;
